import { Injectable } from '@angular/core';
import {
  HttpEvent,
  HttpRequest,
  HttpHandler,
  HttpInterceptor,
  HttpResponse,
  HttpErrorResponse,
  HttpHeaders,
} from '@angular/common/http';
import { Observable, of, throwError, Subject } from 'rxjs';
import { NetworkService } from './network.service';
import { AppStorageService } from './app-storage.service';

@Injectable()
export class CacheInterceptor implements HttpInterceptor {
  private _cache = new Map<string, any>();
  private _offlineQueue: { request: HttpRequest<any>; timestamp: number }[] = [];

  constructor(
    private networkService: NetworkService,
    private storage: AppStorageService,
  ) {
    this.init();

    // Process offline queue when coming back online
    this.networkService.getOnlineStatus().subscribe((online) => {
      if (online) {
        this.processOfflineQueue();
      }
    });
  }

  private init(): void {
    // Defer offline queue load to avoid blocking main thread during page load
    if ('requestIdleCallback' in window) {
      requestIdleCallback(() => this.loadOfflineQueue(), { timeout: 2000 });
    } else {
      setTimeout(() => this.loadOfflineQueue(), 100);
    }
  }

  cache() {
    return {
      set: async (key: string, value: any) => {
        try {
          const _value = {
            value,
            expiry: new Date().getTime() + 60000 * 60 * 24 * 7, // 7 days (extended from 1 day)
          };
          await this.storage.setItem(`cache_${key}`, _value);
          this._cache.set(key, _value);
        } catch (error) {
          console.error('[CacheInterceptor] Error setting cache:', error);
          // Fallback to in-memory cache only
          const _value = {
            value,
            expiry: new Date().getTime() + 60000 * 60 * 24 * 7,
          };
          this._cache.set(key, _value);
        }
      },
      get: async (key: string): Promise<HttpResponse<any> | null> => {
        try {
          const item = (await this.storage.getItem(`cache_${key}`)) || this._cache.get(key);
          if (!item) {
            return null;
          }
          // compare the expiry time of the item with the current time
          if (new Date().getTime() > item.expiry) {
            // If the item is expired, delete the item from storage
            // and return null
            await this.storage.removeItem(`cache_${key}`);
            this._cache.delete(key);
            return null;
          }
          // Reconstruct HttpResponse with proper headers
          const cachedResponse = item.value;

          // Reconstruct HttpHeaders from serialized data
          let headers = new HttpHeaders();
          if (cachedResponse.headers && cachedResponse.headers.headers) {
            const headerMap = cachedResponse.headers.headers;
            Object.keys(headerMap).forEach((key) => {
              if (headerMap[key] && headerMap[key].length > 0) {
                headers = headers.set(key, headerMap[key]);
              }
            });
          }

          return new HttpResponse({
            body: cachedResponse.body,
            headers: headers,
            status: cachedResponse.status,
            statusText: cachedResponse.statusText,
            url: cachedResponse.url,
          });
        } catch (error) {
          console.error('[CacheInterceptor] Error getting cache:', error);
          // Fallback to in-memory cache
          const item = this._cache.get(key);
          if (!item) {
            return null;
          }
          if (new Date().getTime() > item.expiry) {
            this._cache.delete(key);
            return null;
          }
          // Reconstruct HttpResponse with proper headers
          const cachedResponse = item.value;

          // Reconstruct HttpHeaders from serialized data
          let headers = new HttpHeaders();
          if (cachedResponse.headers && cachedResponse.headers.headers) {
            const headerMap = cachedResponse.headers.headers;
            Object.keys(headerMap).forEach((key) => {
              if (headerMap[key] && headerMap[key].length > 0) {
                headers = headers.set(key, headerMap[key]);
              }
            });
          }

          return new HttpResponse({
            body: cachedResponse.body,
            headers: headers,
            status: cachedResponse.status,
            statusText: cachedResponse.statusText,
            url: cachedResponse.url,
          });
        }
      },
      clear: async () => {
        try {
          // Clear in-memory cache
          this._cache.clear();
          // Clear storage cache (only cache_ prefixed items)
          const keys = await this.storage.keys();
          const cacheKeys = keys.filter((key) => key.startsWith('cache_'));
          await Promise.all(cacheKeys.map((key) => this.storage.removeItem(key)));
        } catch (error) {
          console.error('[CacheInterceptor] Error clearing cache:', error);
          // Fallback to clearing in-memory cache only
          this._cache.clear();
        }
      },
    };
  }

  /**
   * Add a request to the offline queue
   */
  private async addToOfflineQueue(request: HttpRequest<any>): Promise<void> {
    // Only queue POST, PUT, DELETE requests
    if (['POST', 'PUT', 'DELETE'].includes(request.method)) {
      this._offlineQueue.push({
        request: request.clone(),
        timestamp: new Date().getTime(),
      });

      try {
        // Store queue in storage
        const queueData = this._offlineQueue.map((item) => ({
          url: item.request.url,
          method: item.request.method,
          body: item.request.body,
          timestamp: item.timestamp,
          headers: Array.from(item.request.headers.keys()).reduce((headers, key) => {
            headers[key] = item.request.headers.get(key);
            return headers;
          }, {}),
        }));
        await this.storage.setItem('offline_queue', queueData);
      } catch (error) {
        console.error('[CacheInterceptor] Error saving offline queue:', error);
      }
    }
  }

  /**
   * Load offline queue from storage
   */
  private async loadOfflineQueue(): Promise<void> {
    try {
      const queueData = await this.storage.getItem('offline_queue');
      if (queueData && Array.isArray(queueData)) {
        try {
          this._offlineQueue = queueData.map((item: any) => {
            // Convert plain headers object to HttpHeaders
            const httpHeaders = new HttpHeaders();
            if (item.headers) {
              Object.keys(item.headers).forEach((key) => {
                if (item.headers[key]) {
                  httpHeaders.set(key, item.headers[key]);
                }
              });
            }

            return {
              request: new HttpRequest(item.method, item.url, item.body, { headers: httpHeaders }),
              timestamp: item.timestamp,
            };
          });
        } catch (e) {
          console.error('[CacheInterceptor] Failed to parse offline queue', e);
          this._offlineQueue = [];
        }
      }
    } catch (error) {
      console.error('[CacheInterceptor] Error loading offline queue:', error);
      this._offlineQueue = [];
    }
  }

  /**
   * Process offline queue when coming back online
   */
  private async processOfflineQueue(): Promise<void> {
    if (this._offlineQueue.length === 0) return;

    console.log(`[CacheInterceptor] Processing ${this._offlineQueue.length} offline requests`);

    // Process queue in order of timestamp (oldest first)
    const queue = [...this._offlineQueue].sort((a, b) => a.timestamp - b.timestamp);
    this._offlineQueue = [];

    try {
      await this.storage.removeItem('offline_queue');
    } catch (error) {
      console.error('[CacheInterceptor] Error clearing offline queue:', error);
    }

    // Execute each request
    queue.forEach((item) => {
      // Use the Angular HTTP mechanism rather than creating a handler
      fetch(item.request.url, {
        method: item.request.method,
        headers: item.request.headers as any,
        body: item.request.body,
        credentials: 'include',
      })
        .then((response) => {
          console.log(
            `[CacheInterceptor] Offline request to ${item.request.url} processed with status ${response.status}`,
          );
        })
        .catch((error) => {
          console.error(`[CacheInterceptor] Failed to process offline request to ${item.request.url}`, error);
          // Re-add to the queue if it failed
          this.addToOfflineQueue(item.request);
        });
    });
  }

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    // If offline, try to serve from cache or queue the request
    if (this.networkService.isOffline()) {
      // For GET requests, try to serve from cache
      if (request.method === 'GET') {
        // Convert async cache.get to Observable
        return new Observable((observer) => {
          this.cache()
            .get(request.url)
            .then((cachedResponse) => {
              if (cachedResponse) {
                observer.next(cachedResponse);
                observer.complete();
              } else {
                // If not in cache, return a network error
                observer.error(
                  new HttpErrorResponse({
                    error: 'offline',
                    status: 0,
                    statusText: 'No internet connection',
                    url: request.url,
                  }),
                );
              }
            })
            .catch((error) => {
              observer.error(
                new HttpErrorResponse({
                  error: 'offline',
                  status: 0,
                  statusText: 'No internet connection',
                  url: request.url,
                }),
              );
            });
        });
      }

      // For non-GET requests, add to offline queue
      this.addToOfflineQueue(request);

      // Return a temporary success response
      return of(
        new HttpResponse({
          status: 202,
          statusText: 'Accepted for offline processing',
          url: request.url,
        }),
      );
    }

    // Online requests pass through. Catalogs are cached by ApiService.readCatalog, not by this interceptor.
    return next.handle(request);
  }
}
